/* eslint-disable @typescript-eslint/no-shadow */
/* eslint-disable i18next/no-literal-string,react/require-default-props */
import React, {
  ComponentType,
  forwardRef,
  Ref,
  useCallback,
  useEffect,
  useState,
} from 'react';
import {
  InputAttributes,
  NumberFormatValues,
  PatternFormat,
  PatternFormatProps,
} from 'react-number-format';

import classNames from 'classnames';

import { Input } from '@ast/magma/components/input';

import { CountrySelectorControl } from '@app/common/components/controls/CountrySelectorControl/CountrySelectorControl';
import { DEFAULT_COUNTRY } from '@app/common/types/locales';
import { Country, findCountryByCode } from '@app/common/utils/countries';
import { preparePhoneValue, getPhoneFormat } from '@app/common/utils/format/phone/formatPhoneNumber';
import { useIsMobile } from '@app/common/utils/hooks/useIsMobile';

import styles from './PhoneInputControl.pcss';

type PhoneInputControlProps = Omit<PatternFormatProps, 'format' | 'onChange'> & {
  readonly displayType?: string,
  readonly format?: string;
  readonly label?: string | null;
  readonly displayDialCode?: boolean; // can be applied when displayType = text
  readonly inputDialCode?: boolean;
  readonly controlMode?: 'controlled' | 'uncontrolled';
  readonly onChange?: (value: string) => void;
};

export const PhoneInputControl = forwardRef((
  props : PhoneInputControlProps,
  ref: Ref<PatternFormatProps>,
) => {
  const {
    format,
    displayType,
    onChange,
    value,
    displayDialCode,
    inputDialCode,
    controlMode = 'controlled',
    ...restProps
  } = props;
  const isMobile = useIsMobile();
  const defaultCountry: Country = findCountryByCode(DEFAULT_COUNTRY) as Country;
  const [country, setCountry] = useState<Country>(defaultCountry);
  const [phoneNumber, setPhoneNumber] = useState<string>(
    typeof value === 'number' ? value?.toString() : value ?? '',
  );

  const handleCountryChange = useCallback((country: Country) => {
    setCountry(country);
    if (onChange) {
      const phoneValue = inputDialCode
        ? country.dialCode + phoneNumber
        : phoneNumber;

      onChange(phoneValue);
    }
  }, [phoneNumber, inputDialCode]);

  const handlePhoneNumberChange = ({ formattedValue }: NumberFormatValues) => {
    const newPhoneNumber = formattedValue
      .replace(/[()]/g, '')
      .replace(/\s/g, '-');

    setPhoneNumber(newPhoneNumber);

    if (onChange) {
      onChange(inputDialCode ? country.dialCode + newPhoneNumber : newPhoneNumber);
    }
  };

  useEffect(() => {
    if (controlMode === 'uncontrolled') {
      setPhoneNumber(typeof value === 'number' ? value?.toString() : value ?? '');
    }
  }, [value]);

  return (
    <div className={classNames(styles.wrapper, inputDialCode && styles.withDialCode)}>
      {inputDialCode && (
        <CountrySelectorControl
          className={styles.countryCode}
          defaultValue={country}
          dropVerticalAlignment={isMobile ? 'top' : 'bottom'}
          onChange={handleCountryChange}
        />
      )}
      <div className={styles.phoneNumber}>
        <PatternFormat
          getInputRef={ref}
          inputMode="decimal"
          displayType={displayType}
          customInput={Input as ComponentType<InputAttributes> | undefined}
          format={getPhoneFormat(phoneNumber, format, displayDialCode)}
          value={preparePhoneValue(phoneNumber)}
          valueIsNumericString
          onValueChange={handlePhoneNumberChange}
          {...restProps}
        />
      </div>
    </div>
  );
});

PhoneInputControl.defaultProps = {
  displayType: 'input',
  format: undefined,
  label: '',
};
